import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { useAuth } from '../auth/AuthContext';

/**
 * Banner persistente que aparece no topo do AppShell quando o usuário
 * tem etapas pendentes do onboarding pós-aceite de convite (Sprint 5
 * Equipe).
 *
 * UX:
 *   - Fica visível enquanto há etapas REQUIRED incompletas
 *   - Etapas opcionais aparecem na lista mas não bloqueiam o banner
 *   - "Dispensar" some por essa sessão (sessionStorage) — reaparece em
 *     próximo login
 *   - Click no item → navega pra rota relevante
 *
 * Backend: GET /users/me/onboarding retorna `{ steps, complete }`.
 */

interface Step {
  id: string;
  label: string;
  done: boolean;
  required: boolean;
  link: string;
}

interface OnboardingStatus {
  steps: Step[];
  complete: boolean;
  completedCount: number;
  totalCount: number;
}

const DISMISS_KEY = 'onboarding-banner-dismissed';

export function OnboardingBanner() {
  const { user } = useAuth();
  // Dismiss por usuário/tenant — não vazar entre contas no mesmo browser.
  const dismissKey = user?.tenantId
    ? `${DISMISS_KEY}.${user.tenantId}`
    : DISMISS_KEY;
  const [expanded, setExpanded] = useState(false);
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem(dismissKey) === '1',
  );

  // E2E (Playwright/Selenium) suprime o banner inteiro pra evitar
  // race de layout — o banner aparece async após `useQuery`, podendo
  // empurrar elementos clicáveis no meio dos testes. Em produção,
  // `navigator.webdriver` é sempre false.
  const isE2E =
    typeof navigator !== 'undefined' && navigator.webdriver === true;

  const { data } = useQuery<OnboardingStatus>({
    queryKey: ['onboarding-status'],
    queryFn: async () => (await api.get('/users/me/onboarding')).data,
    refetchOnWindowFocus: !isE2E,
    staleTime: 60_000,
    retry: 1,
    enabled: !isE2E,
  });

  if (isE2E) return null;
  if (!data || data.complete || dismissed) return null;

  const requiredPending = data.steps.filter((s) => s.required && !s.done);
  const optionalPending = data.steps.filter((s) => !s.required && !s.done);

  return (
    <div
      className="bg-amber-50 border-b border-amber-200"
      data-testid="onboarding-banner"
    >
      <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <span className="text-amber-700 text-lg">⚙️</span>
          <div>
            <p className="text-sm font-medium text-amber-900">
              Complete seu cadastro
            </p>
            <p className="text-xs text-amber-800">
              {data.completedCount} de {data.totalCount} etapas concluídas —{' '}
              {requiredPending.length} obrigatórias pendentes.
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            data-testid="onboarding-expand"
            className="px-3 py-1.5 text-xs rounded-md bg-amber-600 text-white hover:bg-amber-700"
          >
            {expanded ? 'Ocultar' : 'Ver etapas'}
          </button>
          <button
            type="button"
            onClick={() => {
              sessionStorage.setItem(dismissKey, '1');
              setDismissed(true);
            }}
            data-testid="onboarding-dismiss"
            className="px-3 py-1.5 text-xs rounded-md text-amber-800 hover:bg-amber-100"
          >
            Dispensar
          </button>
        </div>
      </div>

      {expanded && (
        <div className="max-w-7xl mx-auto px-4 pb-3">
          <ul
            className="bg-white rounded-md border border-amber-200 divide-y divide-amber-100"
            data-testid="onboarding-steps"
          >
            {[...requiredPending, ...optionalPending, ...data.steps.filter((s) => s.done)].map(
              (step) => (
                <li
                  key={step.id}
                  className="flex items-center justify-between gap-3 px-4 py-2.5"
                >
                  <div className="flex items-center gap-3">
                    <span
                      className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-xs ${
                        step.done
                          ? 'bg-emerald-100 text-emerald-700'
                          : step.required
                            ? 'bg-amber-200 text-amber-800'
                            : 'bg-ink-100 text-ink-500'
                      }`}
                    >
                      {step.done ? '✓' : '◯'}
                    </span>
                    <div>
                      <p
                        className={`text-sm ${
                          step.done
                            ? 'text-ink-500 line-through'
                            : 'text-ink-800 font-medium'
                        }`}
                      >
                        {step.label}
                      </p>
                      {!step.required && (
                        <p className="text-[10px] text-ink-500">Opcional</p>
                      )}
                    </div>
                  </div>
                  {!step.done && (
                    <Link
                      to={step.link}
                      data-testid={`onboarding-step-${step.id}`}
                      className="px-3 py-1 text-xs rounded-md border border-amber-300 text-amber-800 hover:bg-amber-50"
                    >
                      Configurar →
                    </Link>
                  )}
                </li>
              ),
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
